import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import EventAPI from "../../api/EventApi";
import EventListAPI from "../../api/EventListApi";
import { getEventStatus } from "../../utils/eventStatus";
import { coHostedForTab, countCoHostedByTab, mergeHostEvents } from "../utils/eventHosts";

// Custom hook to fetch tab counts via aggregation queries
export const useTabCounts = (globalState) => {
  const [counts, setCounts] = useState({
    upcoming: null,
    completed: null,
    cancelled: null,
    suggestions: null,
    hosts: null,
    requests: null,
  });
  // Auth warm-up runs this effect twice (cached vendorID first, isSuperAdmin
  // once the fresh claim/vendor read lands), so two scopes' aggregations can
  // be in flight together and last-to-land would win. Version each run and
  // drop responses from a superseded scope.
  const scopeVersionRef = useRef(0);

  useEffect(() => {
    const scopeVersion = ++scopeVersionRef.current;
    if (!globalState.vendorID) return;
    // GIS-228. Same settle guard as the list fetch: without it these six
    // aggregations also ran twice, and the discarded host-scoped set competed
    // with the list query for connections during the load window.
    if (!globalState.adminSettled) return;
    const fetchCounts = async () => {
      const resolved = await EventListAPI.getTabCounts({
        isSuperAdmin: globalState.isSuperAdmin,
        vendorID: globalState.vendorID,
      });
      if (scopeVersion !== scopeVersionRef.current) return;

      // GIS-231. The upcoming tab is queried as isCompleted==false, but a
      // cancelled event ALSO keeps isCompleted:false, so the raw count counted
      // rows the list deliberately does not render — the strip said 12 next to
      // 9 cards. Cancelled is therefore a subset of upcoming here, and
      // subtracting it is exactly the set the rows show. If cancelling ever
      // starts setting isCompleted, this stops being true and the two numbers
      // will drift apart again.
      if (typeof resolved.upcoming === "number" && typeof resolved.cancelled === "number") {
        resolved.upcoming = Math.max(0, resolved.upcoming - resolved.cancelled);
      }

      // GIS-222. A host's list also shows the events they co-host, so the tab
      // numbers count them too. Super admins already count every event.
      if (!globalState.isSuperAdmin) {
        const coHosted = countCoHostedByTab(await EventAPI.getCoHostedEvents(globalState.vendorID));
        if (scopeVersion !== scopeVersionRef.current) return;
        ["upcoming", "completed", "cancelled"].forEach((tab) => {
          if (typeof resolved[tab] === "number") resolved[tab] += coHosted[tab];
        });
      }

      setCounts(prev => ({ ...prev, ...resolved }));
    };
    // GIS-228. Deferred past first paint. These six counts only feed the small
    // numbers in the tab strip — nothing above the fold waits on them — but
    // firing them on mount put six aggregation round trips in contention with
    // the query that actually renders the page.
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(fetchCounts, { timeout: 2000 })
      : window.setTimeout(fetchCounts, 0);
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
    };
  }, [globalState.vendorID, globalState.isSuperAdmin, globalState.adminSettled]);

  return counts;
};

// Custom hook for events data fetching and state management
export const useEvents = (globalState, filters, activeTab) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Auth warm-up fires the reset effect twice — cached vendorID first, then
  // isSuperAdmin when the fresh claim/vendor read lands — so two resets with
  // different query scopes run concurrently. Each is two hops (getDocs + the
  // getEventsRevenue callable), and on a functions cold start the first
  // fetch's callable can outlive the whole second fetch, letting the stale
  // host-scoped response overwrite the super-admin list and poison lastDoc.
  // Fetches capture the version at start and bail once a newer reset begins.
  const fetchVersionRef = useRef(0);
  const itemsPerPage = 10;

  const fetchEvents = useCallback(async (reset = false) => {
    // Bump before the vendorID guard so a scope change (e.g. sign-out)
    // invalidates in-flight fetches even when the new scope can't fetch.
    if (reset) fetchVersionRef.current += 1;
    if (!globalState.vendorID) return;
    // GIS-228. Hold until the super-admin check has SETTLED (succeeded or failed). Before
    // this, a cold load fired a host-scoped query first (0 rows for an
    // operator), discarded it as stale, and only then ran the real one — two
    // getDocs and two revenue callables to render one list. adminResolved is
    // set by the same GlobalContext read that decides isSuperAdmin, so waiting
    // costs nothing that the second fetch was not already waiting for. Settled
    // rather than resolved: a failed read must end the wait without also being
    // read as an authorization answer (see GlobalContext).
    if (!globalState.adminSettled) return;
    if (!reset && (!hasMore || loadingMore || loading)) return;

    if (reset) {
      setLoading(true);
      // An in-flight pagination fetch is stale now and bails without
      // clearing its own flag, which would wedge infinite scroll.
      setLoadingMore(false);
    } else {
      setLoadingMore(true);
    }
    const fetchVersion = fetchVersionRef.current;
    const isStale = () => fetchVersion !== fetchVersionRef.current;

    try {
      // Query shape (tab predicates, host scoping, the GIS-231 index notes)
      // lives in EventListAPI.getEventsPage.
      const page = await EventListAPI.getEventsPage({
        isSuperAdmin: globalState.isSuperAdmin,
        vendorID: globalState.vendorID,
        tab: activeTab,
        cursor: !reset && lastDoc ? lastDoc : null,
        pageSize: itemsPerPage,
      });
      if (isStale()) return;
      const eventsData = page.events;

      // Cursor for the next page is the last DISPLAYED doc, not the lookahead doc.
      if (page.lastDoc) {
        setLastDoc(page.lastDoc);
      }

      setHasMore(page.hasMore);

      // Apply client-side filters (status filtering)
      let filteredEvents = eventsData;
      if (filters.status) {
        filteredEvents = filteredEvents.filter(event => getEventStatus(event).status === filters.status);
      }

      // GIS-228. Paint FIRST. This used to await getEventsRevenue before
      // setLoading(false), which put a Cloud Function cold start directly inside
      // LCP: the skeleton stayed up, and the LCP image could not even begin
      // downloading until the callable returned. The rows have everything they
      // need to render except the revenue badge.
      // Rows paint before their revenue is known, so flag the batch as pending
      // and let the badge shimmer until the callable below settles it. Without
      // the flag the amount just pops in a beat later and reads as a glitch.
      const paintedEvents = filteredEvents.map((e) => ({ ...e, revenuePending: true }));

      if (reset) {
        setEvents(paintedEvents);
        setLoading(false);
      } else {
        setEvents(prev => [...prev, ...paintedEvents]);
        setLoadingMore(false);
      }

      // GIS-181 Track B: revenue lives in the gated events/{id}/finance/summary doc
      // now; a bulk list query can't join it, so batch-fetch via the host/superAdmin
      // getEventsRevenue callable and merge onto the events so the card/table badges
      // render from event.revenue exactly as before — just a beat later, by id
      // rather than by rebuilding the array, so a page appended in the meantime
      // is not clobbered.
      const ids = filteredEvents.map((e) => e.id);
      // Settles only this batch: a page appended while the callable was in
      // flight is still waiting on its own, and must keep shimmering.
      const settleRevenue = (revById) => {
        const batch = new Set(ids);
        setEvents(prev =>
          prev.map(e => {
            if (!batch.has(e.id)) return e;
            const settled = { ...e, revenuePending: false };
            if (revById && revById[e.id]) settled.revenue = revById[e.id];
            return settled;
          })
        );
      };
      try {
        if (ids.length) {
          const revById = await EventAPI.getEventsRevenue(ids);
          if (isStale()) return;
          settleRevenue(revById);
        }
      } catch (revErr) {
        console.error("Error enriching events with revenue:", revErr);
        // A failed lookup has to land on "no revenue" rather than leave the
        // badges shimmering forever.
        if (isStale()) return;
        settleRevenue(null);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      // A stale fetch's spinner flags belong to the newer reset now.
      if (isStale()) return;
      if (reset) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, [globalState.vendorID, globalState.isSuperAdmin, globalState.adminSettled, activeTab, lastDoc, hasMore, loadingMore, loading, filters.status]);

  useEffect(() => {
    setLastDoc(null);
    setHasMore(true);
    setEvents([]);
    fetchEvents(true);
  }, [globalState.vendorID, globalState.isSuperAdmin, globalState.adminSettled, activeTab, filters.hostname, filters.eventName, filters.startDate, filters.endDate]);

  // GIS-222. Events this host co-hosts, fetched once per signed-in scope and
  // merged into whichever tab is showing. A co-host is never sent the event's
  // revenue (getEventsRevenue answers for the primary host only), and there are
  // few enough of these that they need neither paging nor an index.
  const [coHosted, setCoHosted] = useState([]);
  useEffect(() => {
    let cancelled = false;
    setCoHosted([]);
    if (!globalState.vendorID || !globalState.adminSettled || globalState.isSuperAdmin) return undefined;
    EventAPI.getCoHostedEvents(globalState.vendorID).then((rows) => {
      if (!cancelled) setCoHosted(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [globalState.vendorID, globalState.isSuperAdmin, globalState.adminSettled]);

  const mergedEvents = useMemo(() => {
    if (globalState.isSuperAdmin || coHosted.length === 0) return events;
    let rows = coHostedForTab(coHosted, activeTab);
    if (filters.status) rows = rows.filter((event) => getEventStatus(event).status === filters.status);
    return mergeHostEvents(events, rows, activeTab);
  }, [events, coHosted, activeTab, filters.status, globalState.isSuperAdmin]);

  return { events: mergedEvents, loading, hasMore, loadingMore, fetchEvents };
};

// Custom hook for infinite scroll
export const useInfiniteScroll = (fetchEvents, hasMore, loadingMore, loading) => {
  const observerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchEvents();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    const node = observerRef.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [fetchEvents, hasMore, loadingMore, loading]);

  return observerRef;
};
